import React from 'react';
import { Input, Popover, Select } from 'antd';
import type { SelectProps } from 'antd';
import { useController, useFormContext } from 'react-hook-form';
import type { Path } from 'react-hook-form';
import clsx from 'clsx';
import type { ClientFormValues } from '../../../../types/ClientManagementTypes';
import styles from './ClientFormModal.module.scss';

// Local rather than the shared InputField: this wizard needs dark styling that antd's error status would repaint.

interface FieldShellProps {
  label: string;
  /** Sits beside the label, as the mockups place the ⓘ on the subscription fields. */
  info?: React.ReactNode;
  error?: string;
  className?: string;
  children: React.ReactNode;
}

const FieldShell = ({ label, info, error, className, children }: FieldShellProps) => (
  <div className={clsx(styles.field, className)}>
    <label className={styles.fieldLabel}>
      {label}
      {info}
    </label>
    {children}
    {/* Always rendered so an appearing error can't shift the layout. */}
    <span className={styles.errorSlot}>{error}</span>
  </div>
);

export const InfoIcon = ({ content }: { content: React.ReactNode }) => (
  <Popover content={content} arrow={false} placement="bottomLeft" rootClassName={styles.infoOverlay}>
    <img src="/assets/info-icon.svg" alt="" className={styles.infoIcon} />
  </Popover>
);

interface TextInputFieldProps {
  name: Path<ClientFormValues>;
  label: string;
  placeholder: string;
  info?: React.ReactNode;
  disabled?: boolean;
  maxLength?: number;
  /** Drops anything that isn't a digit as the user types or pastes. */
  digitsOnly?: boolean;
  /** Applied to the field's own wrapper, not the input -- e.g. to narrow it in a shared row. */
  className?: string;
}

export const TextInputField = ({ name, label, placeholder, info, disabled, maxLength, digitsOnly, className }: TextInputFieldProps) => {
  const { field, fieldState } = useController<ClientFormValues>({ name });
  const { clearErrors } = useFormContext<ClientFormValues>();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Continue raises the errors; typing clears them rather than leaving a stale message.
    if (fieldState.error) clearErrors(name);
    const next = digitsOnly ? event.target.value.replace(/\D/g, '') : event.target.value;
    field.onChange(maxLength ? next.slice(0, maxLength) : next);
  };

  return (
    <FieldShell label={label} info={info} error={fieldState.error?.message} className={className}>
      <Input
        {...field}
        value={(field.value as string) ?? ''}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        inputMode={digitsOnly ? 'numeric' : undefined}
        className={clsx(styles.textInput, { [styles.hasError]: fieldState.error })}
      />
    </FieldShell>
  );
};

// The step scrolls, so the menu has to render outside it.
const selectPopupContainer = () => document.body;

// A solid triangle, matching the stepper's own footer carets.
const selectCaret = <span className={styles.selectCaret} />;

interface SelectFieldProps {
  name: Path<ClientFormValues>;
  options: SelectProps['options'];
  placeholder: string;
  disabled?: boolean;
  onSelected?: (value: string) => void;
}

interface SelectInputFieldProps extends SelectFieldProps {
  label: string;
  info?: React.ReactNode;
}

export const SelectInputField = ({ name, label, info, options, placeholder, disabled, onSelected }: SelectInputFieldProps) => {
  const { field, fieldState } = useController<ClientFormValues>({ name });
  const { clearErrors } = useFormContext<ClientFormValues>();

  // The step owns the cross-field rules, so the change is handed to it rather than written here.
  const handleChange = (value: string) => {
    if (fieldState.error) clearErrors(name);
    if (onSelected) {
      onSelected(value);
      return;
    }
    field.onChange(value);
  };

  return (
    <FieldShell label={label} info={info} error={fieldState.error?.message}>
      <Select
        {...field}
        value={(field.value as string) || undefined}
        onChange={handleChange}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        suffixIcon={selectCaret}
        getPopupContainer={selectPopupContainer}
        popupClassName={styles.selectPopup}
        className={clsx(styles.selectControl, { [styles.hasError]: fieldState.error })}
      />
    </FieldShell>
  );
};

/** The add-on row's length control: the same select without a label or an error slot. */
export const TermSelect = ({ name, options, placeholder, disabled, onSelected }: SelectFieldProps) => {
  const { field } = useController<ClientFormValues>({ name });

  return (
    <Select
      {...field}
      value={(field.value as string) || undefined}
      onChange={onSelected ?? field.onChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      suffixIcon={selectCaret}
      getPopupContainer={selectPopupContainer}
      popupClassName={styles.selectPopup}
      // The control is narrower than "2 years - Expires December 3, 2026".
      popupMatchSelectWidth={false}
      className={clsx(styles.selectControl, styles.termSelect)}
    />
  );
};
